import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { CancelOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { ActionItemKeys, RejectedCollaborativeAgreement } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { trackToDoCTA } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { BaseDashboardActionItem, collaborativeAgreementRejectionReasonText, UploadCollaborativeAgreementModal } from '@uplift/ui-v2';
import { CamelCasedPropertiesDeep } from 'type-fest';

export type CollabAgreementRejectedProps = {
  agreement: CamelCasedPropertiesDeep<RejectedCollaborativeAgreement>;
  onDismiss: (agreementId: string) => void;
};

export const CollabAgreementRejected: React.VFC<CollabAgreementRejectedProps> = ({ agreement, onDismiss }) => {
  const [ uploadModalOpen, setUploadModalOpen ] = useState(false);
  const me = useCurrentuser();
  const queryClient = useQueryClient();

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    queryClient.invalidateQueries({ queryKey: [ 'allActionItems' ] });
  };

  const reasonText = collaborativeAgreementRejectionReasonText(agreement.rejectionReason, agreement.rejectionDetails);

  const handleUploadClick = () => {
    trackToDoCTA({
      toDoType: ActionItemKeys.PROVIDER_COLLABORATIVE_AGREEMENT_REJECTED,
      userType: UserRole.provider
    });
    setUploadModalOpen(true);
  };

  return (
    <>
      <BaseDashboardActionItem
        icon={<CancelOutlined />}
        color="danger"
        title="Collaborative agreement rejected"
        body={(
          <>
            <Typography variant="body2" color="textPrimary">
              {agreement.stateName ? `${agreement.stateName} License` : 'License'}: {reasonText}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Review the reason and resubmit your updated agreement.
            </Typography>
          </>
        )}
        action={{
          text: 'Upload New PDF',
          handleClick: handleUploadClick
        }}
        dismiss={{
          text: 'Mark as Viewed',
          handleClick: () => onDismiss(agreement.agreementId)
        }}
      />
      {uploadModalOpen && (
        <UploadCollaborativeAgreementModal
          onClose={handleUploadModalClose}
          license={{
            id: agreement.licenseId,
            stateId: agreement.stateId,
            userId: me.id
          }}
        />
      )}
    </>
  );
};
